import {DownloadBackordersApi} from '@/api/download-backorders';

/**
 * Download timeout (value in milliseconds).
 * @type {number}
 */
const DownloadTimeout = 120000; // 2 minutes

/**
 * Time to wait before next status verification.
 * @type {number}
 */
const VerifyStatusDelay = 5000; // 5 seconds

/**
 * Delay to enable download button again after successful download.
 * @type {number}
 */
const ProceedWithDownloadDelay = 5000; // 5 seconds

const DownloadBackorders = {
  watch: {
    isDownloading(value) {
      if (value) {
        this.clearTimeouts();
        this.downloadInProgress();
      }
    },
  },

  methods: {
    /**
     * Get an object with options for data export.
     * @return {{hideUnallocated: DownloadBackorders.methods.hideUnallocated, hideLinesWithoutComments: DownloadBackorders.methods.hideLinesWithoutComments, sortBy: *, hidePreseason: DownloadBackorders.methods.hidePreseason, type: *}}
     */
    getExportOptions() {
      return {
        hideUnallocated: this.hideUnallocated,
        hideLinesWithoutComments: this.hideLinesWithoutComments,
        hidePreseason: this.hidePreseason,
        type: this.currentTab,
        sortBy: this.configSortByForURL,
      };
    },

    /**
     * Export backorders.
     */
    async downloadBackorders() {
      this.isDownloading = true;
      try {
        const options = this.getExportOptions();
        const exportId = await DownloadBackordersApi.obtainExportId(options);

        await this.waitUntilExportReadyForDownload(exportId);
        this.downloadFile(exportId);
        this.clearTimeouts();

        setTimeout(this.downloadIsSuccessful, ProceedWithDownloadDelay);
      } catch (e) {
        this.downloadHasFailed();
        console.error(e);
      }
    },

    /**
     * Wait until the exported file is ready to be downloaded.
     * @return {Promise<string|null>}
     */
    waitUntilExportReadyForDownload(exportId) {
      if (!exportId) {
        throw new Error('Export ID is invalid');
      }

      return new Promise((resolve, reject) => {
        const verifyStatus = () => {
          DownloadBackordersApi.isFileReady(exportId).then((isReady) => {
            if (!isReady) {
              this.checkStatusTimeout = setTimeout(verifyStatus, VerifyStatusDelay);
            } else {
              resolve();
            }
          });
        };

        // Start verification for maximum wait time.
        this.downloadTimeout = setTimeout(() => reject(new Error('Reached wait timeout')), DownloadTimeout);

        // Check download status.
        this.checkStatusTimeout = setTimeout(verifyStatus, VerifyStatusDelay);
      });
    },

    /**
     * Redirect to the file for download.
     * @param exportId
     */
    downloadFile(exportId) {
      if (!exportId) {
        throw new Error('Export ID is invalid');
      }

      window.open(DownloadBackordersApi.getFileDownloadUrl(exportId), '_self');
    },

    /**
     * Set download status - in progress.
     */
    downloadInProgress() {
      this.downloadStatus = {
        isVisible: true,
        type: 'primary',
        message: 'backordersAllocated.B2B.downloading',
      };
    },

    /**
     * Set download status - download is successful.
     */
    downloadIsSuccessful() {
      this.isDownloading = false;
      this.downloadStatus = {
        isVisible: true,
        type: 'success',
        message: 'backordersAllocated.B2B.ready',
      };
    },

    /**
     * Set download status - display error message.
     */
    downloadHasFailed() {
      this.isDownloading = false;
      this.downloadStatus = {
        isVisible: true,
        type: 'alert',
        message: 'errorMessage.unableToProcess',
      };
    },

    /**
     * Clear checking and download timeouts.
     */
    clearTimeouts() {
      if (this.checkStatusTimeout) {
        clearTimeout(this.checkStatusTimeout);
      }

      if (this.downloadTimeout) {
        clearTimeout(this.downloadTimeout);
      }
    },
  },

  data() {
    return {
      checkStatusTimeout: null,
      downloadTimeout: null,
      exportId: '',
      downloadStatus: {
        isVisible: false,
        type: '',
        message: '',
      },
      isDownloading: false,
    };
  },
  beforeDestroy() {
    this.clearTimeouts();
  },
};

export default DownloadBackorders;
